import { omit } from 'lodash';
import { getNormalizedDateTime } from 'lib/timeHelpers';
import { isDefined, isEmptyString } from 'lib/typeguards';
import { getMinsSinceMidnight } from 'components/perks/award-enrichments/utils';
import {
    AdjustmentScheme,
    AdjustmentSchemeMutationBody,
    AdjustmentSchemeType
} from '../adjustmentSchemeModel';
import { ConfigureSchemeGeneralFormData } from '../forms/AdjustmentSchemeGeneralForm';
import { AdjustmentSchemeRedemptionFormData } from '../forms/redemption-form/AdjustmentSchemeRedemptionForm';

export const getAdjustmentSchemeMutationBody = (
    data: {
        general: ConfigureSchemeGeneralFormData;
        redemption: AdjustmentSchemeRedemptionFormData;
    },
    merchantTimezone?: string,
    previousScheme?: AdjustmentScheme
): AdjustmentSchemeMutationBody => {
    const { general, redemption } = data;

    const hasDiscountFields =
        !!redemption.discount && !!Object.values(redemption.discount).filter(Boolean).length;
    const hasBasketQualificationFields =
        !!redemption.basketQualification &&
        !!Object.values(redemption.basketQualification).filter(Boolean).length;

    let { startDate, endDate } = redemption.dateTimeRestriction;
    const startTimeMins = getMinsSinceMidnight(redemption.dateTimeRestriction.startTime) ?? 0;
    const endTimeMins = getMinsSinceMidnight(redemption.dateTimeRestriction.endTime) ?? 0;

    if (startDate) {
        startDate = getNormalizedDateTime(startDate, merchantTimezone)
            .set({ hour: Math.floor(startTimeMins / 60), minute: startTimeMins % 60 })
            .toUTC()
            .toISO();
    }

    if (endDate) {
        endDate = endTimeMins
            ? getNormalizedDateTime(endDate, merchantTimezone)
                  .set({ hour: Math.floor(endTimeMins / 60), minute: endTimeMins % 60 })
                  .toUTC()
                  .toISO()
            : getNormalizedDateTime(endDate, merchantTimezone).endOf('day').toUTC().toISO();
    }

    const weekDays = redemption.dateTimeRestriction.daysOfWeek?.length
        ? redemption.dateTimeRestriction.daysOfWeek.reduce((acc, weekday) => {
              acc += Number(weekday);

              return acc;
          }, 0)
        : undefined;

    // Remove uneditable fields. If API changes, update here
    const previousSchemeCopy = omit(previousScheme, ['_id', 'tenantId', '__v']);

    const hasCustomDiscountMaxCount =
        isDefined(redemption.discount?.scope) &&
        redemption.discount?.scope !== 'BASKET' &&
        isDefined(redemption.discount?.rule) &&
        (redemption.discount?.rule === 'CHEAPEST' || redemption.discount?.rule === 'COSTLIEST') &&
        isDefined(redemption.discount?.maxCount) &&
        redemption.discount.maxCount > 1;

    const dateTimeRestrictionObj = {
        ...(previousSchemeCopy.dateTimeRestriction || {}),
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        startTime: startTimeMins * 60 || undefined,
        endTime: endTimeMins * 60 || undefined,
        daysOfWeek: weekDays
    };

    const hasDateTimeRestrictionFields = Object.values(dateTimeRestrictionObj).filter(Boolean).length > 0;

    return {
        ...(previousSchemeCopy || {}),
        title: general.title,
        internalTitle: general.internalTitle,
        schemeId: general.schemeId,
        schemeType: AdjustmentSchemeType.COUPON,
        externalPosId: general.externalPosId,
        locationIds: general.locationIds,
        enabled: general.enabled,
        excludeDiscountIds: general.excludeDiscountIds.length > 0 ? general.excludeDiscountIds : [],
        discount: hasDiscountFields
            ? {
                  ...(previousSchemeCopy.discount || {}),
                  value:
                      isDefined(redemption.discount.value) && !isEmptyString(redemption.discount.value)
                          ? Number(redemption.discount.value)
                          : undefined,
                  scope: redemption.discount.scope,
                  type: redemption.discount.type,
                  scopeMatchCode: redemption.discount.scopeMatchCode,
                  rule: redemption.discount.rule || 'CHEAPEST',
                  childItemRule: redemption.discount.childItemRule || 'INCLUDE',
                  maxCount: hasCustomDiscountMaxCount ? redemption.discount.maxCount : undefined,
                  combinationRule: redemption.discount.combinationRule
              }
            : undefined,
        basketQualification: hasBasketQualificationFields
            ? {
                  ...(previousSchemeCopy.basketQualification || {}),
                  matchCode: redemption.basketQualification.matchCode || undefined,
                  minimumItems:
                      redemption.basketQualification.minimumItems ||
                      (hasBasketQualificationFields && !redemption.basketQualification.minimumValue && 1) ||
                      undefined,
                  minimumValue: redemption.basketQualification.minimumValue || undefined,
                  scope: redemption.basketQualification.scope,
                  maxCount: redemption.basketQualification.maxCount || undefined
              }
            : undefined,
        dateTimeRestriction: hasDateTimeRestrictionFields ? dateTimeRestrictionObj : {}
    };
};
